/**
 * Shapes and pure helpers for the admin Statistiques page (#645): periods in
 * Montréal calendar days, the response the page reads, and the small
 * calculations both sides share. The SQL is lib/admin-analytics.server.ts.
 */

export const ANALYTICS_PERIODS = [7, 30, 90] as const;

export type AnalyticsPeriod = (typeof ANALYTICS_PERIODS)[number];

export const DEFAULT_ANALYTICS_PERIOD: AnalyticsPeriod = 30;

/** Days are Montréal days, like the crons and the PDF analytics. */
export const ANALYTICS_TIME_ZONE = "America/Montreal";

/** Where a scan came from: a manual link, a PDF QR code, or a printer poster's link. */
export type ScanSource = "link" | "pdf" | "poster";

export interface DailyPoint {
  codesScanned: number;
  /** yyyy-mm-dd, Montréal day. */
  date: string;
  link: number;
  pdf: number;
  poster: number;
  shareVisits: number;
  shareDownloads: number;
}

export interface Totals {
  scans: number;
  shareVisits: number;
  shareDownloads: number;
  /** Distinct QR codes scanned at least once. */
  codesScanned: number;
}

export interface AnalyticsItemPerson {
  id: string;
  name: string;
  email: string;
  /** Display URL of the profile picture (BetterAuth user.image), when set. */
  avatarUrl: string | null;
}

/**
 * A person from a `user` row, or null when the row is missing. `image` may
 * still hold the raw `avatars/<file>` R2 key on backfilled rows, so it maps to
 * the blob proxy URL the same way GET /api/admin/users does.
 */
export function toAnalyticsPerson(
  id: string | null,
  name: string | null,
  email: string | null,
  image: string | null,
): AnalyticsItemPerson | null {
  if (id === null) return null;

  return {
    id,
    name: name || email || "",
    email: email ?? "",
    avatarUrl: image?.replace(/^avatars\//, "/api/blob/avatar/") ?? null,
  };
}

export interface TopCode {
  id: string;
  source: ScanSource;
  name: string;
  /** Null for a poster's link, and for a code whose owner's account is gone. */
  owner: AnalyticsItemPerson | null;
  scans: number;
  /** Scans in the previous period, for the change. */
  previous: number;
  /** One value per day of the period, oldest first. */
  daily: number[];
}

export interface TopShare {
  id: string;
  name: string;
  owner: AnalyticsItemPerson | null;
  visits: number;
  downloads: number;
}

export interface BreakdownItem {
  /** Raw value ("mobile", "CA", "hamel.ca"); null when the scan carried none. */
  key: string | null;
  value: number;
}

export interface AnalyticsSummary {
  days: AnalyticsPeriod;
  /** First and last Montréal day of the period, yyyy-mm-dd. */
  from: string;
  to: string;
  current: Totals;
  previous: Totals;
  daily: DailyPoint[];
  /**
   * The same number of days just before the period, aligned index for index
   * with `daily`: the previous period drawn behind the current one.
   */
  previousDaily: DailyPoint[];
  topCodes: TopCode[];
  topShares: TopShare[];
  breakdowns: {
    devices: BreakdownItem[];
    browsers: BreakdownItem[];
    systems: BreakdownItem[];
    countries: BreakdownItem[];
    referrers: BreakdownItem[];
  };
  /** Scans per Montréal hour of day, index 0 to 23. */
  hours: number[];
}

/** What a row of "Les plus actifs" can be: a QR code of any source, or a share. */
export type AnalyticsItemKind = ScanSource | "share";

export const ANALYTICS_ITEM_KINDS = ["link", "pdf", "poster", "share"] as const;

/** What the row dialog shows about a top code or share: who owns it and what it is. */
export interface AnalyticsItemDetail {
  kind: AnalyticsItemKind;
  id: string;
  name: string;
  /** Null for a link whose owner's account is gone, and for poster-managed links. */
  owner: AnalyticsItemPerson | null;
  project: { id: string; name: string; number: string | null } | null;
  createdAt: string | null;
  lastAccessed: string | null;
  /** Soft-deleted since it was scanned. */
  deleted: boolean;
  /** Lifetime totals, not the period's. */
  views: number;
  downloads: number | null;
  /** Link and poster codes: where a scan lands today. */
  destination: string | null;
  /** The printer or Wi-Fi poster that owns a poster-source link. */
  poster: { id: string; name: string } | null;
  share: {
    expiresAt: string | null;
    locked: boolean;
    fileCount: number;
    totalSize: number;
  } | null;
  /** Server clock, so expiry reads the same as on the share pages. */
  now: number;
}

/** The period a query string asks for; anything else falls back to the default. */
export function parseAnalyticsPeriod(value: string | number | null | undefined): AnalyticsPeriod {
  const days = Number(value);

  return ANALYTICS_PERIODS.find((period) => period === days) ?? DEFAULT_ANALYTICS_PERIOD;
}

const dayFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: ANALYTICS_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** The Montréal calendar day of an instant, yyyy-mm-dd. */
export function montrealDay(at: Date): string {
  return dayFormatter.format(at);
}

/** Calendar arithmetic on yyyy-mm-dd strings (noon UTC, so no DST edge). */
export function addDays(day: string, amount: number): string {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + amount);

  return date.toISOString().slice(0, 10);
}

export interface PeriodBounds {
  today: string;
  /** First day of the period. */
  start: string;
  /** First day of the period before it (same length). */
  previousStart: string;
  /** The day after today: exclusive upper bound. */
  end: string;
}

/** The last `days` Montréal days including today, and the same span before it. */
export function periodBounds(days: number, now = new Date()): PeriodBounds {
  const today = montrealDay(now);
  const start = addDays(today, -(days - 1));

  return { today, start, previousStart: addDays(start, -days), end: addDays(today, 1) };
}

/** Every day from `start` to `today`, oldest first. */
export function daysBetween(start: string, today: string): string[] {
  const days: string[] = [];

  for (let day = start; day <= today; day = addDays(day, 1)) days.push(day);

  return days;
}

/** Change against the previous period, as a whole percent; null when there is nothing to compare with. */
export function changePercent(current: number, previous: number): number | null {
  if (previous === 0) return null;

  return Math.round(((current - previous) / previous) * 100);
}

/**
 * The biggest `limit` items, the rest folded into one "other" item so the
 * bars still add up. Ties keep their order; empty in, empty out.
 */
export function topWithOthers(items: BreakdownItem[], limit: number): BreakdownItem[] {
  const sorted = [...items].sort((a, b) => b.value - a.value);

  if (sorted.length <= limit + 1) return sorted;
  const rest = sorted.slice(limit).reduce((sum, item) => sum + item.value, 0);

  return [...sorted.slice(0, limit), { key: OTHER_KEY, value: rest }];
}

/** Marks the folded "other" bucket of topWithOthers. */
export const OTHER_KEY = "__other__";

/** What the SQL returns, before it is shaped for the page. */
export interface RawAnalytics {
  /** Scans per day and source, over the period and the one before it. */
  scanDaily: Array<{ day: string; source: ScanSource; n: number }>;
  /** Share activity per day, over the period and the one before it. */
  shareDaily: Array<{ day: string; download: boolean; n: number }>;
  codesScanned: { current: number; previous: number };
  /** Distinct codes scanned per day, over the period and the one before it. */
  codesScannedDaily: Array<{ day: string; n: number }>;
  topCodes: Array<Omit<TopCode, "daily">>;
  /** Daily scans of the top codes, over the period. */
  topCodeDaily: Array<{ id: string; day: string; n: number }>;
  topShares: TopShare[];
  breakdowns: AnalyticsSummary["breakdowns"];
  hours: Array<{ hour: number; n: number }>;
}

const emptyTotals = (): Totals => ({
  scans: 0,
  shareVisits: 0,
  shareDownloads: 0,
  codesScanned: 0,
});

/** Shapes the raw rows into the page's summary: filled days, period totals, per-code trends. */
export function assembleAnalytics(
  days: AnalyticsPeriod,
  bounds: PeriodBounds,
  raw: RawAnalytics,
): AnalyticsSummary {
  const emptyPoint = (date: string): DailyPoint => ({
    date,
    link: 0,
    pdf: 0,
    poster: 0,
    shareVisits: 0,
    shareDownloads: 0,
    codesScanned: 0,
  });

  const daily = daysBetween(bounds.start, bounds.today).map(emptyPoint);

  const previousDaily = daysBetween(bounds.previousStart, addDays(bounds.start, -1)).map(
    emptyPoint,
  );

  const byDay = new Map([...previousDaily, ...daily].map((point) => [point.date, point]));
  const current = emptyTotals();
  const previous = emptyTotals();

  for (const row of raw.scanDaily) {
    const inPeriod = row.day >= bounds.start;
    (inPeriod ? current : previous).scans += row.n;
    const point = byDay.get(row.day);

    if (point) point[row.source] += row.n;
  }

  for (const row of raw.shareDaily) {
    const inPeriod = row.day >= bounds.start;
    const totals = inPeriod ? current : previous;

    if (row.download) totals.shareDownloads += row.n;
    else totals.shareVisits += row.n;
    const point = byDay.get(row.day);

    if (point) point[row.download ? "shareDownloads" : "shareVisits"] += row.n;
  }

  current.codesScanned = raw.codesScanned.current;
  previous.codesScanned = raw.codesScanned.previous;

  for (const row of raw.codesScannedDaily) {
    const point = byDay.get(row.day);

    if (point) point.codesScanned = row.n;
  }

  const perCode = new Map<string, Map<string, number>>();

  for (const row of raw.topCodeDaily) {
    const series = perCode.get(row.id) ?? new Map<string, number>();
    series.set(row.day, row.n);
    perCode.set(row.id, series);
  }

  const hours = Array.from({ length: 24 }, () => 0);

  for (const row of raw.hours) {
    if (row.hour >= 0 && row.hour < 24) hours[row.hour] = row.n;
  }

  return {
    days,
    from: bounds.start,
    to: bounds.today,
    current,
    previous,
    daily,
    previousDaily,
    topCodes: raw.topCodes.map((code) => ({
      ...code,
      daily: daily.map((point) => perCode.get(code.id)?.get(point.date) ?? 0),
    })),
    topShares: raw.topShares,
    breakdowns: raw.breakdowns,
    hours,
  };
}
